import { SubmitHandler, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { getSessionId, getTableNumber, updateName, getName } from "../redux/slices";
import { useEffect, useRef, useState } from "react";
import { useAppDispatch } from "../redux/hooks";
import { useSearchParams } from "react-router-dom";
import { Loading } from "./Loading";
import { trackEvent } from "../utils/analytics";
import "./playerEntryName.scss";

type FormValues = {
  name: string;
};

export const PlayerName: React.FC = () => {
  const savedName = useSelector(getName);
  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
    watch,
  } = useForm<FormValues>({
    defaultValues: { name: savedName ?? "" },
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [animated, setAnimated] = useState(false);
  const hasStartedTyping = useRef<boolean>(false);
  const hasReachedLimit = useRef<boolean>(false);
  const hasFocusedNickname = useRef<boolean>(false);
  const [searchParams] = useSearchParams();
  const sessionId = useSelector(getSessionId);
  const tableNumber = useSelector(getTableNumber);

  const dispatch = useAppDispatch();
  const nav = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 600);
    return () => clearTimeout(timer);
  }, []);

  // if the redux store is missing a session ID (or the session ID does not match the URL parameter),
  // then redirect to the start page. Suspect that this is a shared link and this "new" user needs to start at the beginning of the journey
  let sessionIdParam = searchParams.get("s");
  let tableNumberParam = searchParams.get("t");
  let venueCode = searchParams.get("v");

  useEffect(() => {
    if (sessionId === null || tableNumber === null) {
      nav(`/?s=${sessionIdParam}&t=${tableNumberParam}&v=${venueCode}`);
    }

    if (sessionId !== sessionIdParam || tableNumber !== tableNumberParam) {
      nav(`/?s=${sessionIdParam}&t=${tableNumberParam}&v=${venueCode}`);
    } else {
      setLoading(false);
    }
  }, [sessionId, tableNumber, venueCode, sessionIdParam, tableNumberParam, nav]);

  // END of "copied link" checking

  const nameValue = watch("name", "");

  useEffect(() => {
    if (nameValue?.length > 0 && !hasStartedTyping.current) {
      hasStartedTyping.current = true;
      trackEvent({
        gaEvent: "nickname_typing_started",
        clarityEvent: "nickname_typing_started",
        params: { venue_code: venueCode },
      });
    }
    if (nameValue?.length >= 10 && !hasReachedLimit.current) {
      hasReachedLimit.current = true;
      trackEvent({
        gaEvent: "nickname_character_limit_reached",
        params: { venue_code: venueCode },
      });
    }
  }, [nameValue, venueCode]);

  const { onChange: nameOnChange, ...nameRegister } = register("name", {
    required: true,
    maxLength: { value: 10, message: "Max 10 characters" },
    validate: (value) => !/\p{Extended_Pictographic}/u.test(value) || "No emojis allowed",
  });

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    trackEvent({
      gaEvent: "click_accept_nickname",
      clarityEvent: "click_accept_nickname",
      params: { venue_code: venueCode },
    });
    setLoading(true);
    dispatch(updateName(data.name));
    nav(`/photo?s=${sessionIdParam}&t=${tableNumberParam}&v=${venueCode}`);
  };

  if (loading) return <Loading />;

  return (
    <div className={`pageContainer nicknamePage${animated ? " animate" : ""}`}>
      <div className="nickname-logo">
        <img src={"./img/fc-logo.svg"} alt="Flight Club" />
      </div>

      <div className="nickname-tagline">
        <div className="welcome-sub">JOIN THE GAME</div>
        <div className="welcome-sub-small">in 3 steps</div>
      </div>

      <div className="nickname-reveal">
        <div className="contentCard-outer">
          <div className="contentCard">
            <div className="formContainer">
              <form data-clarity-mask="true" id="nicknameForm" onSubmit={handleSubmit(onSubmit)}>
                <div className="formHeader">
                  <div className="step">Step 1/3</div>
                  <div className="title">
                    Your <br /> player name
                  </div>
                </div>
                <div className="nicknameInputWrapper">
                  <input
                    className="nicknameInput"
                    type="text"
                    placeholder="Enter here"
                    maxLength={10}
                    autoComplete="off"
                    {...nameRegister}
                    onChange={(e) => {
                      e.target.value = e.target.value
                        .replace(
                          /[\p{Extended_Pictographic}\p{Emoji_Modifier}\u200D\uFE0F\u20E3]|\p{Regional_Indicator}{2}/gu,
                          "",
                        )
                        .slice(0, 10);
                      nameOnChange(e);
                    }}
                    onFocus={() => {
                      if (!hasFocusedNickname.current) {
                        hasFocusedNickname.current = true;
                        trackEvent({
                          clarityEvent: "click_in_nickname_field",
                          params: { venue_code: venueCode },
                        });
                      }
                    }}
                  />
                </div>
                <div className="inputHint">10 characters max, no emojis</div>
                {errors.name && <div className="inputError">{errors.name.message}</div>}
              </form>
            </div>
          </div>
          <span className="corner-bottom-left" />
          <span className="corner-bottom-right" />
        </div>
        <footer className="btn-container">
          <button
            type="submit"
            form="nicknameForm"
            className="with-forward-arrow"
            disabled={!isValid}
            onClick={handleSubmit(onSubmit)}
          >
            NEXT <img className="forward-arrow" src="./img/forward-arrow.svg" alt="" />
          </button>
        </footer>
      </div>
    </div>
  );
};
